import React, { useEffect, useState } from 'react';
import { PropertyNote } from '@/types/index';
import { propertyService } from '@services/property.service';
import { Spinner } from '@components/common/Spinner';

interface NotesTabProps {
  propertyId: string;
}

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const NotesTab: React.FC<NotesTabProps> = ({ propertyId }) => {
  const [notes, setNotes] = useState<PropertyNote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newContent, setNewContent] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadNotes = async () => {
    try {
      setIsLoading(true);
      const data = await propertyService.getNotes(propertyId);
      setNotes(data);
    } catch (err) {
      console.error('Erreur chargement notes:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadNotes();
  }, [propertyId]);

  const handleAdd = async () => {
    if (!newContent.trim()) return;
    try {
      setIsAdding(true);
      const note = await propertyService.createNote(propertyId, newContent.trim());
      setNotes([note, ...notes]);
      setNewContent('');
    } catch (err) {
      console.error('Erreur ajout note:', err);
    } finally {
      setIsAdding(false);
    }
  };

  const handleUpdate = async (noteId: string) => {
    if (!editContent.trim()) return;
    try {
      const updated = await propertyService.updateNote(noteId, editContent.trim());
      setNotes(notes.map(n => n.id === noteId ? updated : n));
      setEditingId(null);
      setEditContent('');
    } catch (err) {
      console.error('Erreur modification note:', err);
    }
  };

  const handleDelete = async (noteId: string) => {
    try {
      await propertyService.deleteNote(noteId);
      setNotes(notes.filter(n => n.id !== noteId));
      setDeletingId(null);
    } catch (err) {
      console.error('Erreur suppression note:', err);
    }
  };

  const startEdit = (note: PropertyNote) => {
    setEditingId(note.id);
    setEditContent(note.content);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Zone d'ajout */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">
          Ajouter une note
        </h3>
        <textarea
          value={newContent}
          onChange={(e) => setNewContent(e.target.value)}
          placeholder="Saisissez votre note ici..."
          rows={3}
          maxLength={5000}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {newContent.length}/5000
          </span>
          <button
            onClick={handleAdd}
            disabled={isAdding || !newContent.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            {isAdding ? 'Ajout...' : 'Ajouter'}
          </button>
        </div>
      </div>

      {/* Liste des notes */}
      {notes.length === 0 ? (
        <div className="text-center py-8 text-gray-400 dark:text-gray-500 text-sm">
          Aucune note pour cette propri&eacute;t&eacute;
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <div
              key={note.id}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4"
            >
              {editingId === note.id ? (
                /* Mode édition */
                <div>
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={3}
                    maxLength={5000}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => handleUpdate(note.id)}
                      disabled={!editContent.trim()}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 rounded-lg transition-colors"
                    >
                      Enregistrer
                    </button>
                    <button
                      onClick={() => { setEditingId(null); setEditContent(''); }}
                      className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                /* Mode lecture */
                <div>
                  <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                    {note.content}
                  </p>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                    <div className="text-xs text-gray-400 dark:text-gray-500">
                      <span>Cr&eacute;&eacute; le {formatDate(note.createdAt)}</span>
                      {note.updatedAt !== note.createdAt && (
                        <span className="ml-2">
                          &middot; Modifi&eacute; le {formatDate(note.updatedAt)}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => startEdit(note)}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                      >
                        Modifier
                      </button>
                      {deletingId === note.id ? (
                        <span className="flex gap-1">
                          <button
                            onClick={() => handleDelete(note.id)}
                            className="text-xs text-red-600 dark:text-red-400 font-medium hover:text-red-800 transition-colors"
                          >
                            Confirmer
                          </button>
                          <button
                            onClick={() => setDeletingId(null)}
                            className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
                          >
                            Annuler
                          </button>
                        </span>
                      ) : (
                        <button
                          onClick={() => setDeletingId(note.id)}
                          className="text-xs text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                        >
                          Supprimer
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
